import { getLoadedEnv } from '@myre/common-conf';
import { RemoteError } from '@myre/common-core';
import { LogType } from '@myre/common-logger';
import { isErrorResponseBody } from '@myre/common-primitives';
import {
  DynamicEnvService,
  MyreApiError,
  SdkHttpErrorResponse,
} from '@myre/ui-domain-http';

import { HttpErrorResponse } from '@angular/common/http';
import {
  EnvironmentInjector,
  ErrorHandler,
  inject,
  Injectable,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { addBreadcrumb, captureException } from '@sentry/angular';

import { getLatestValue } from '../../helpers/observable.helpers';

import { loggedUserSelector, type AuthState } from '../auth/auth.service';

/** An NgRx action carrying the error it failed with. */
function isActionError(
  error: unknown,
): error is { type: string; error: unknown } {
  return (
    typeof error === 'object' &&
    error !== null &&
    'type' in error &&
    typeof error.type === 'string' &&
    'error' in error
  );
}

/** A failed HTTP call: what the SDK throws, or what Angular throws for a call made outside it. */
type HttpFailure = HttpErrorResponse | MyreApiError;

/**
 * The name of the endpoint a failed call was sent to: its API and the route
 * as the API declares it when the SDK made the call, the host and the path
 * for a call made outside it.
 */
export function getEndpoint(failure: HttpFailure): string {
  if (
    failure instanceof MyreApiError ||
    failure instanceof SdkHttpErrorResponse
  ) {
    return `${failure.api} ${failure.route}`;
  }

  if (!failure.url) {
    return 'unknown';
  }

  const baseUrl =
    typeof window === 'undefined' ? undefined : window.location.origin;
  const { host, pathname } = new URL(failure.url, baseUrl);

  return `${host} ${pathname}`;
}

interface DescribedFailure {
  message: string;
  cause: Error | undefined;
  status: number;
  myreErrorCode: number | undefined;
  responseBody: unknown;
}

function describeFailure(
  failure: HttpFailure,
  endpoint: string,
): DescribedFailure {
  if (failure instanceof MyreApiError) {
    const { status, code, message, content, meta } = failure;

    return {
      message: `Myre ERROR ${code} from ${endpoint}: ${message}`,
      cause: failure,
      status: failure.httpStatus,
      myreErrorCode: code,
      responseBody: { status, code, message, content, meta },
    };
  }

  const body: unknown = failure.error;
  const myreError = isErrorResponseBody(body) ? body : undefined;
  const remoteError = myreError?.meta?.error;

  let message: string;

  if (failure.status === 0) {
    message = `No response from ${endpoint}`;
  } else if (myreError) {
    message = `Myre ERROR ${myreError.code} from ${endpoint}: ${myreError.message}`;
  } else {
    message = `HTTP ${failure.status} ${failure.statusText} from ${endpoint}`;
  }

  return {
    message,
    cause: remoteError ? new RemoteError(remoteError) : undefined,
    status: failure.status,
    myreErrorCode: myreError?.code,
    responseBody: body,
  };
}

/**
 * A failed HTTP call, named by what failed: the endpoint, the status — 0 when
 * no response came back (offline, aborted, refused) — and, for an error the
 * Myre API answered, its code and message. Its `cause` is the error thrown
 * where the call was made, and below it the error the API raised it from,
 * down to where the chain of requests started.
 */
export class HttpRequestError extends Error {
  public readonly status: number;
  public readonly endpoint: string;
  public readonly myreErrorCode: number | undefined;
  /** The body the call was answered with, `null` when it had none. */
  public readonly responseBody: unknown;

  constructor(failure: HttpFailure) {
    const endpoint = getEndpoint(failure);
    const { message, cause, status, myreErrorCode, responseBody } =
      describeFailure(failure, endpoint);

    super(message, cause ? { cause } : undefined);

    this.name = 'HttpRequestError';
    this.status = status;
    this.endpoint = endpoint;
    this.myreErrorCode = myreErrorCode;
    this.responseBody = responseBody;
  }

  /**
   * A positive Myre code names the failure, raised from that endpoint: a code
   * shared by many endpoints (a validation error) is one issue per endpoint.
   * Anything else is one issue per failing endpoint and status.
   */
  public get fingerprint(): string[] {
    return this.myreErrorCode !== undefined && this.myreErrorCode > 0
      ? ['myre-error', String(this.myreErrorCode), this.endpoint]
      : ['http-error', String(this.status), this.endpoint];
  }
}

/** The error an Angular error report is about, as Sentry should see it. */
export function toReportedError(error: unknown): unknown {
  const actualError = isActionError(error) ? error.error : error;

  return actualError instanceof HttpErrorResponse ||
    actualError instanceof MyreApiError
    ? new HttpRequestError(actualError)
    : actualError;
}

// Matches cross-browser failed dynamic-import messages caused by a stale bundle after deploy.
const STALE_CHUNK_ERROR =
  /Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|ChunkLoadError/i;

export function isStaleChunkError(error: unknown): boolean {
  let message = '';

  if (error instanceof Error) {
    message = error.message;
  } else if (typeof error === 'string') {
    message = error;
  }

  return STALE_CHUNK_ERROR.test(message);
}

@Injectable({ providedIn: 'root' })
export class CustomErrorHandler extends ErrorHandler {
  private readonly _dynamicEnvService = inject(DynamicEnvService);
  private readonly _injector = inject(EnvironmentInjector);

  public override handleError(error: any): void {
    if (isStaleChunkError(error) && this._reloadOnceForStaleChunk()) {
      return;
    }

    const store = this._injector.get(Store<AuthState>);
    const loggedUser = getLatestValue(store.select(loggedUserSelector));

    if (this._dynamicEnvService.env.errors.sentry) {
      const reportedError = toReportedError(error);

      const user = loggedUser && {
        ...loggedUser,
        id: loggedUser.id.toString(),
      };

      captureException(
        reportedError,
        reportedError instanceof HttpRequestError
          ? {
              user,
              fingerprint: reportedError.fingerprint,
              tags: {
                'http.status': reportedError.status,
                'http.endpoint': reportedError.endpoint,
                myreErrorCode: reportedError.myreErrorCode,
              },
              extra: { response: reportedError.responseBody },
            }
          : { user },
      );
    }

    if (this._dynamicEnvService.env.errors.console) {
      super.handleError(error);
    }
  }

  // Returns true and reloads once; debounce via sessionStorage prevents a reload loop.
  private _reloadOnceForStaleChunk(): boolean {
    if (typeof window === 'undefined') {
      return false;
    }

    const STORAGE_KEY = 'myre.staleChunkReloadAt';
    const RELOAD_DEBOUNCE_MS = 10_000;
    const now = Date.now();

    try {
      const lastReloadAt = Number(
        window.sessionStorage.getItem(STORAGE_KEY) ?? 0,
      );

      if (now - lastReloadAt < RELOAD_DEBOUNCE_MS) {
        return false;
      }

      window.sessionStorage.setItem(STORAGE_KEY, String(now));
    } catch {
      return false;
    }

    this._reload();

    return true;
  }

  // Isolated to allow stubbing in tests.
  protected _reload(): void {
    window.location.reload();
  }
}

export function logMessage(
  logType: LogType,
  message: string,
  details?: any,
): void {
  const env = getLoadedEnv();

  if (env.errors.sentry) {
    addBreadcrumb({
      message,
      category: logType,
      level: 'info',
      data: { details },
    });
  }

  if (env.errors.console) {
    // @ts-expect-error, eslint-disable-next-line no-console
    console.log(`[${LogType[logType]}]`, message, details);
  }
}
